import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from './AxiosConfig'; // Ensure AxiosConfig has baseURL set
import HomePage from './components/HomePage';
import Login from './components/Login';
import RegisterUser from './components/RegisterUser';
import CreateEditOrg from './components/CreateEditOrg';
import Management from './components/Admin';
import SetPassword from './components/SetPassword';
import MyOrg from './components/MyOrg';
import Sidebar from './components/Sidebar';
import ForgotPassword from './components/ForgotPassword';
import ApproveOrganization from './components/ApproveOrg';
import 'bootstrap/dist/css/bootstrap.min.css';

const PrivateRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get('/api/auth/status');
        setAuthStatus(response.data.isAuthenticated);
      } catch (error) {
        setAuthStatus(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading) return <div>Loading...</div>; // Loading state
  return authStatus ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get('/api/auth/status');
        setAuthStatus(response.data.isAuthenticated);
        setIsAdmin(response.data.isAdmin);
      } catch (error) {
        setAuthStatus(false);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading) return <div>Loading...</div>; 
  
  if (!authStatus) {
    return <Navigate to="/login" replace />; 
  }
  
  if (!isAdmin) {
    return <Navigate to="/myOrg" replace />; 
  }

  return children; 
};

function App() {
  return (
    <Router>
      <Layout />
    </Router>
  );
}

const Layout = () => {
  const location = useLocation();
  const pagesWithCollapsedSidebar = ['/login', '/forgot-password', '/register'];
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [readyToRender, setReadyToRender] = useState(false);

  useEffect(() => {
    const shouldCollapse = pagesWithCollapsedSidebar.includes(location.pathname);
    setIsCollapsed(shouldCollapse);
    setReadyToRender(true);
  }, [location]);

  if (!readyToRender) return null;

  return (
    <div className="d-flex">
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      <div
        className="flex-grow-1"
        style={{
          marginLeft: isCollapsed ? '0' : '250px',
          transition: 'margin-left 0.3s ease-in-out',
        }}
      >
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterUser />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/setPassword/:token" element={<SetPassword />} />

          {/* Protected Routes (Authenticated Users) */}
          <Route path="/createEditOrg" element={ <PrivateRoute> <CreateEditOrg /> </PrivateRoute>}/>
          <Route path="/myOrg" element={<PrivateRoute> <MyOrg /> </PrivateRoute>}/>

          {/* Admin Only Routes */}
          <Route path="/admin" element={<AdminRoute> <Management /> </AdminRoute>}/>
          <Route path="/admin/approveOrg/:orgId" element={ <AdminRoute> <ApproveOrganization /> </AdminRoute>}/>

          {/* Catch-All Route */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </div>
  );
};

export default App;
